/**
 * modifiers.ts — aggregates every multiplier source into one inspectable set,
 * and manages the time-limited buff stack (events, boosts).
 *
 * Multipliers are MULTIPLICATIVE across sources. `global` multiplies essentially
 * everything; the category-specific factors stack on top for their domain.
 */
import type { GameState, ActiveModifier } from '../core/GameState';
import { safeNumber } from '../core/numbers';
import {
  BALANCE,
  UPGRADE_BY_ID,
  TECH_BY_ID,
  SKILL_BY_ID,
  ACHIEVEMENT_BY_ID,
  ASCENSION_PERK_BY_ID,
  ORES,
  MASTERY,
  CHALLENGE_BY_ID,
  type UpgradeEffect,
} from '../data';
import { oreMasteryTier } from './mastery';

export interface Multipliers {
  click: number;
  auto: number;
  value: number;
  smelt: number;
  energy: number;
  worker: number;
  global: number;
}

function emptyMults(): Multipliers {
  return { click: 1, auto: 1, value: 1, smelt: 1, energy: 1, worker: 1, global: 1 };
}

const KEY: Record<UpgradeEffect['type'], keyof Multipliers> = {
  clickMult: 'click',
  autoMult: 'auto',
  valueMult: 'value',
  smeltMult: 'smelt',
  energyMult: 'energy',
  workerMult: 'worker',
  globalMult: 'global',
};

/** A single labeled multiplier contribution, for the breakdown tooltip. */
export interface MultSource {
  label: string;
  channel: keyof Multipliers;
  factor: number;
}

type SourceVisitor = (channel: keyof Multipliers, factor: number, label: string) => void;

/**
 * Single source of truth for every multiplier contribution. Both the lean
 * per-tick aggregation and the labeled breakdown consume this, so the tooltip
 * total can never drift from the live multipliers.
 */
function visitSources(state: GameState, visit: SourceVisitor): void {
  for (const id of Object.keys(state.upgrades)) {
    const u = UPGRADE_BY_ID[id];
    if (u && state.upgrades[id]) visit(KEY[u.effect.type], u.effect.value, u.name);
  }
  for (const id of Object.keys(state.research.researched)) {
    const t = TECH_BY_ID[id];
    if (t && state.research.researched[id]) visit(KEY[t.effect.type], t.effect.value, t.name);
  }
  for (const [id, lvl] of Object.entries(state.prestige.skills)) {
    const s = SKILL_BY_ID[id];
    if (s && lvl > 0) visit(KEY[s.effect.type], Math.pow(s.effect.value, lvl), `${s.name} Lv.${lvl}`);
  }
  for (const id of Object.keys(state.achievements)) {
    const a = ACHIEVEMENT_BY_ID[id];
    if (a && state.achievements[id]) visit('global', a.reward, a.name);
  }
  const pp = 1 + state.prestige.points * BALANCE.prestige.pointMult;
  if (pp !== 1) visit('global', pp, 'Prestige-Punkte');
  const cores = 1 + state.ascension.cores * BALANCE.ascension.coreMult;
  if (cores !== 1) visit('global', cores, 'Aufstiegs-Kerne');
  for (const [id, lvl] of Object.entries(state.ascension.perks)) {
    const p = ASCENSION_PERK_BY_ID[id];
    if (p && lvl > 0) visit(KEY[p.effect.type], Math.pow(p.effect.value, lvl), `${p.name} Lv.${lvl}`);
  }
  for (const mod of state.modifiers) visit(KEY[mod.effect.type], mod.effect.value, mod.label);
  // Ore Mastery: per-ore mining bonus from cumulative amount mined.
  for (const ore of ORES) {
    const tier = oreMasteryTier(state, ore.id);
    if (tier > 0) visit('auto', Math.pow(1 + MASTERY.bonusPerTier, tier), `${ore.name}-Meisterung`);
  }
  // Completed challenges grant permanent rewards.
  for (const id of state.challenges.completed) {
    const c = CHALLENGE_BY_ID[id];
    if (c) visit(KEY[c.reward.type], c.reward.value, `${c.name} (Herausforderung)`);
  }
}

/**
 * Compute the full multiplier set from upgrades, tech, prestige skills,
 * achievements, prestige points, ascension, and active timed modifiers.
 */
export function computeMultipliers(state: GameState): Multipliers {
  const m = emptyMults();
  visitSources(state, (channel, factor) => { m[channel] *= factor; });
  // Guard every channel against NaN/Infinity.
  for (const k of Object.keys(m) as (keyof Multipliers)[]) m[k] = safeNumber(m[k], 1);
  return m;
}

/**
 * Compute the multipliers AND a labeled per-source breakdown for the tooltip.
 * Opt-in (call only when the tooltip is open) — it's not on the per-tick path.
 */
export function computeMultiplierBreakdown(state: GameState): { mults: Multipliers; sources: MultSource[] } {
  const m = emptyMults();
  const sources: MultSource[] = [];
  visitSources(state, (channel, factor, label) => {
    m[channel] *= factor;
    if (factor !== 1) sources.push({ channel, factor, label });
  });
  for (const k of Object.keys(m) as (keyof Multipliers)[]) m[k] = safeNumber(m[k], 1);
  return { mults: m, sources };
}

/** Remove expired modifiers. Returns the ids that ended this tick. */
export function pruneModifiers(state: GameState): string[] {
  const now = state.meta.gameTimeMs;
  const ended: string[] = [];
  state.modifiers = state.modifiers.filter((mod) => {
    if (mod.endsAt <= now) {
      ended.push(mod.id);
      return false;
    }
    return true;
  });
  return ended;
}

/** Add (or refresh) a timed modifier. */
export function addModifier(state: GameState, mod: ActiveModifier): void {
  const existing = state.modifiers.find((m) => m.id === mod.id);
  if (existing) {
    existing.endsAt = Math.max(existing.endsAt, mod.endsAt);
    existing.effect = mod.effect;
    existing.label = mod.label;
  } else {
    state.modifiers.push(mod);
  }
}
